import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { X, Loader2, MessageCircle, Send } from "lucide-react";

/**
 * Two-party thread drawer for a slate interest signal. Distributor
 * and exhibitor can both open — the from_role stamped on each message
 * decides which bubble side to render on. Non-parties get a 403 from
 * the API and the drawer closes silently.
 *
 * Props:
 *   interest  — {id, film_title, exhibitor_name, distributor_name}
 *   myRole    — 'distributor' | 'exhibitor'  (which side is I)
 *   onClose   — callback to hide the drawer
 *   onSent    — optional callback, called after each successful send
 */
export default function InterestThreadDrawer({ interest, myRole, onClose, onSent }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/interests/${interest.id}/messages`);
      setMsgs(data.messages || []);
    } catch {
      // preserve any locally-visible msgs on transient failure — never
      // wipe them (that caused iter-51 drawer regression).
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [interest.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const clean = body.trim();
    if (!clean) return;
    setBusy(true);
    try {
      await api.post(`/interests/${interest.id}/messages`, { body: clean });
      setBody("");
      // Canonical reload — single source of truth. Prevents the optimistic
      // append from being clobbered by a lagging concurrent GET (iter-51 bug).
      await load();
      if (onSent) onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send.");
    } finally { setBusy(false); }
  };
  const submitOnEnter = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const peerName = myRole === "distributor"
    ? (interest.exhibitor_name || "Exhibitor")
    : (interest.distributor_name || "Distributor");

  // Portal-mount to document.body so no ancestor (transform, filter,
  // backdrop-filter, will-change, contain) can trap our `fixed inset-0`
  // container into a card-sized box. Iter-53 RCA'd this on
  // /slate/discovery where cs-card has backdrop-filter: blur(20px).
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/80"
         onClick={onClose} data-testid="interest-thread-drawer">
      <div className="w-full max-w-md h-full cs-card border-l border-white/10 rounded-none rounded-l-2xl flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-5 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-teal-500/15 text-teal-300 flex items-center justify-center shrink-0">
              <MessageCircle size={18}/>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                {myRole === "distributor" ? "DM to Exhibitor" : "DM from Distributor"}
              </div>
              <div className="font-display text-base uppercase tracking-tight truncate">{peerName}</div>
              <div className="text-[10px] text-white/45 truncate">{interest.film_title}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"
                  data-testid="interest-thread-close">
            <X size={16}/>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3"
             data-testid="interest-thread-messages">
          {loading ? (
            <div className="text-center text-white/45 mt-8">
              <Loader2 size={16} className="animate-spin inline mr-2"/> Loading…
            </div>
          ) : msgs.length === 0 ? (
            <div className="text-center text-white/45 mt-16 px-6">
              <MessageCircle size={30} className="mx-auto mb-3 opacity-40"/>
              <div className="text-sm">
                {myRole === "distributor"
                  ? "Say hello — introduce yourself, share windows available, or ask about their screen count."
                  : "The distributor hasn't replied yet — send them your booking details to start the thread."}
              </div>
            </div>
          ) : (
            msgs.map((m) => (
              <MessageBubble key={m.id} msg={m} mine={m.from_role === myRole}/>
            ))
          )}
          <div ref={endRef}/>
        </div>

        {/* Composer */}
        <div className="p-4 border-t border-white/10 space-y-2">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={submitOnEnter}
                    placeholder="Type your message · Enter to send · Shift+Enter for a new line"
                    className="bg-black/40 border-white/15 min-h-[70px] resize-none"
                    data-testid="interest-thread-input"/>
          <Button className="w-full h-10 rounded-full text-[11px] uppercase tracking-[0.22em] bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-50"
                  disabled={busy || !body.trim()} onClick={send}
                  data-testid="interest-thread-send">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Sending…</> :
              <><Send size={12} className="mr-1.5"/> Send</>}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function MessageBubble({ msg, mine }) {
  const ts = msg.created_at?.slice(11, 16) || "";
  const date = msg.created_at?.slice(0, 10) || "";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}
         data-testid={`interest-thread-msg-${msg.id}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
        mine
          ? "bg-teal-500/25 text-white rounded-tr-sm"
          : "bg-white/8 text-white/90 rounded-tl-sm"
      }`}>
        {!mine && msg.from_name && (
          <div className="text-[9px] tracking-[0.18em] uppercase text-teal-200 mb-0.5">
            {msg.from_name}
          </div>
        )}
        <div className="whitespace-pre-wrap break-words text-[13px] leading-snug">{msg.body}</div>
        <div className={`mt-1 text-[9px] tracking-[0.14em] uppercase ${mine ? "text-white/50" : "text-white/40"}`}>
          {date} · {ts}
        </div>
      </div>
    </div>
  );
}
